interface ToolbarProps {
  color: string;
  width: number;
  onColorChange: (color: string) => void;
  onWidthChange: (width: number) => void;
  onClearCanvas?: () => void;
  showClearButton?: boolean;
  disabled?: boolean;
  isEraser?: boolean;
  onEraserToggle?: () => void;
  onEraserOff?: () => void;
}

const getContrastBorder = (color: string): string => {
  const darkColors = ['#000000', '#0055ff', '#9900ff'];
  if (darkColors.includes(color.toLowerCase())) {
    return '3px solid #ffffff';
  }
  return '3px solid #000000';
};

const COLORS = [
  '#ff0055',
  '#ff9500',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#0055ff',
  '#9900ff',
  '#ff00ff',
  '#ffffff',
  '#000000',
];

const EraserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 20H7L3 16a1 1 0 0 1 0-1.41l10-10a1 1 0 0 1 1.41 0l7 7a1 1 0 0 1 0 1.41L17 17" />
    <path d="m6.5 15.5 5-5" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

export function Toolbar({
  color,
  width,
  onColorChange,
  onWidthChange,
  onClearCanvas,
  showClearButton = false,
  disabled = false,
  isEraser = false,
  onEraserToggle,
  onEraserOff,
}: ToolbarProps) {
  const handleColorClick = (c: string) => {
    onColorChange(c);
    if (isEraser && onEraserOff) {
      onEraserOff();
    }
  };

  return (
    <div className="toolbar">
      <div className="color-picker">
        {COLORS.map((c) => {
          const isSelected = c === color && !isEraser;
          return (
            <button
              key={c}
              className={`color-option ${isSelected ? 'selected' : ''}`}
              style={{
                backgroundColor: c,
                border: isSelected ? getContrastBorder(c) : '2px solid transparent',
                boxSizing: 'border-box',
              }}
              onClick={() => handleColorClick(c)}
              disabled={disabled}
              aria-label={`Select color ${c}`}
            />
          );
        })}
      </div>

      <div className="toolbar-divider" />

      <div className="width-slider">
        <input
          type="range"
          min="2"
          max="20"
          value={width}
          onChange={(e) => onWidthChange(parseInt(e.target.value))}
          disabled={disabled}
        />
        <span>{width}px</span>
      </div>

      <div className="toolbar-divider" />

      {onEraserToggle && (
        <button
          className={`eraser-button ${isEraser ? 'active' : ''}`}
          onClick={onEraserToggle}
          disabled={disabled}
          aria-label={isEraser ? 'Switch to pen' : 'Switch to eraser'}
          title={isEraser ? 'Switch to pen' : 'Eraser'}
        >
          <EraserIcon />
        </button>
      )}

      {showClearButton && onClearCanvas && (
        <button
          className="clear-button"
          onClick={onClearCanvas}
          disabled={disabled}
          aria-label="Clear canvas"
          title="Clear canvas"
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}
